import { ExternalLink, Pencil } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { OrderLine } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useStageFormError } from '../hooks/use-stage-form-error';
import { useRecordParcels } from '../hooks/use-workflow-mutations';
import {
  completesStage,
  isRecorded,
  parcelRow,
  parcelsSchema,
  parcelsToSend,
  pickedLines,
  type OpenLine,
  type ParcelFormRow,
  type ParcelsForm,
} from '../schemas/stage-forms';
import { needsDelayReason } from '../schemas/stage-rules';

import { ActionDialog } from './action-dialog';
import { DelayReasonField } from './delay-reason-field';
import type { StageTarget } from './stage-target';

/**
 * Carrier, tracking number and link per configured parcel. Parcels already recorded are shown as
 * they stand and only re-sent after an explicit Edit with a change, so the ring can ship today
 * and the certificate tomorrow without the shopper seeing the ring "shipped" twice. The stage
 * completes once every parcel is in; only that submit needs a delay reason on a late stage. A new
 * parcel that carries items says which items go in it; several parcels may share them.
 */
export function ParcelsDialog({
  target,
  lines,
  onOpenChange,
}: {
  target: StageTarget;
  /** The order's lines; their open quantities are what the parcels can still ship. */
  lines: readonly OrderLine[];
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('production');
  const format = useFormat();
  const record = useRecordParcels(target.storeId, target.orderId);
  const parcels = target.run.parcels;
  const [editing, setEditing] = useState<ReadonlySet<string>>(() => new Set());
  const [forceDelay, setForceDelay] = useState(false);
  const late = needsDelayReason(target.run) || forceDelay;
  const open = useMemo<OpenLine[]>(
    () =>
      lines
        .map((line) => ({
          id: line.id,
          title: line.variantTitle ? `${line.title} (${line.variantTitle})` : line.title,
          open: line.quantity - line.fulfilledQuantity - line.cancelledQuantity,
        }))
        .filter((line) => line.open > 0),
    [lines],
  );
  const schema = useMemo(
    () => parcelsSchema({ recorded: parcels, editing, requireDelay: late, open }),
    [parcels, editing, late, open],
  );
  const pickers = parcels.filter((p) => p.carriesItems && !isRecorded(p)).length;
  const form = useForm<ParcelsForm>({
    resolver: useI18nResolver(schema),
    defaultValues: {
      parcels: parcels.map((parcel) => parcelRow(parcel, open, pickers > 1)),
      delayReason: '',
    },
  });
  const rows = form.watch('parcels') as ParcelFormRow[];
  // Shown only when this submit would close the stage, which is when the backend asks for it.
  const showDelay = late && completesStage(rows, parcels);
  const known = useMemo(
    () =>
      new Set([
        'parcels',
        'delayReason',
        ...parcels.flatMap((_p, i) =>
          ['carrier', 'trackingNumber', 'trackingUrl', 'key', 'lines'].map(
            (f) => `parcels.${i}.${f}`,
          ),
        ),
      ]),
    [parcels],
  );
  const onError = useStageFormError(form.setError, {
    knownFields: known,
    onDelayReasonRequired: () => setForceDelay(true),
  });
  const parcelsError =
    form.formState.errors.parcels?.message ?? form.formState.errors.parcels?.root?.message;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    const sending = parcelsToSend(values.parcels, { recorded: parcels, editing });
    const indexOf = new Map(values.parcels.map((p, index) => [p.key, index]));
    try {
      await record.mutateAsync({
        key: target.key,
        body: {
          parcels: sending.map((p) => ({
            key: p.key,
            carrier: p.carrier,
            trackingNumber: p.trackingNumber,
            trackingUrl: p.trackingUrl || null,
            ...(p.lines.length > 0 ? { lines: pickedLines(p) } : {}),
          })),
          ...(showDelay && values.delayReason ? { delayReason: values.delayReason } : {}),
        },
      });
      onOpenChange(false);
    } catch (error) {
      // The body only carries the rows sent, so the backend's `parcels[N]` indexes that subset.
      onError(error, (field) =>
        field.replace(
          /^parcels\.(\d+)/,
          (_m, n: string) => `parcels.${indexOf.get(sending[Number(n)]?.key ?? '') ?? n}`,
        ),
      );
    }
  });

  return (
    <Form {...form}>
      <ActionDialog
        open
        wide
        onOpenChange={onOpenChange}
        title={t('parcels.title', { name: target.name })}
        description={t('parcels.description')}
        rootError={form.formState.errors.root?.server?.message}
        isPending={record.isPending}
        submitLabel={t('parcels.submit')}
        submittingLabel={t('parcels.submitting')}
        onSubmit={submit}
      >
        <ol className="flex flex-col gap-4" aria-label={t('parcels.list', { name: target.name })}>
          {parcels.map((parcel, index) => {
            const locked = isRecorded(parcel) && !editing.has(parcel.key);
            return (
              <li
                key={parcel.key}
                aria-label={parcel.label}
                className="flex flex-col gap-3 rounded-md border p-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{parcel.label}</span>
                  {parcel.carriesItems ? (
                    <Badge variant="secondary">{t('parcels.carriesItems')}</Badge>
                  ) : null}
                  {isRecorded(parcel) ? (
                    <Badge variant="outline">{t('parcels.recorded')}</Badge>
                  ) : null}
                </div>
                {locked ? (
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span>
                      {t('parcels.shipped', {
                        carrier: parcel.carrier,
                        number: parcel.trackingNumber,
                      })}
                    </span>
                    {parcel.trackingUrl ? (
                      <a
                        href={parcel.trackingUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 underline underline-offset-2"
                      >
                        {t('parcels.track')}
                        <ExternalLink className="size-3" aria-hidden />
                      </a>
                    ) : null}
                    {parcel.shippedAt ? (
                      <span
                        className="text-muted-foreground text-xs"
                        title={format.utc(parcel.shippedAt)}
                      >
                        {t('parcels.shippedAt', { when: format.relative(parcel.shippedAt) })}
                      </span>
                    ) : null}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={t('parcels.editLabel', { label: parcel.label })}
                      onClick={() => setEditing(new Set([...editing, parcel.key]))}
                    >
                      <Pencil aria-hidden />
                      {t('parcels.edit')}
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-3">
                    <FormField
                      control={form.control}
                      name={`parcels.${index}.carrier`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('parcels.carrier')}</FormLabel>
                          <FormControl>
                            <Input {...field} maxLength={80} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`parcels.${index}.trackingNumber`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('parcels.trackingNumber')}</FormLabel>
                          <FormControl>
                            <Input {...field} maxLength={120} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`parcels.${index}.trackingUrl`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('parcels.trackingUrl')}</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="url"
                              placeholder={t('parcels.trackingUrlPlaceholder')}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
                {!locked && rows[index] && rows[index].lines.length > 0 ? (
                  <fieldset className="flex flex-col gap-2">
                    <legend className="text-sm font-medium">
                      {t('parcels.items', { label: parcel.label })}
                    </legend>
                    {rows[index].lines.map((line, lineIndex) => {
                      const info = open.find((o) => o.id === line.lineId);
                      return (
                        <FormField
                          key={line.lineId}
                          control={form.control}
                          name={`parcels.${index}.lines.${lineIndex}.quantity`}
                          render={({ field, fieldState }) => (
                            <FormItem className="flex flex-row items-center justify-between gap-3">
                              <div className="min-w-0">
                                <FormLabel className="font-normal">{info?.title}</FormLabel>
                                <p className="text-muted-foreground text-xs">
                                  {t('parcels.open', { count: info?.open ?? 0 })}
                                </p>
                                <FormMessage />
                              </div>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="number"
                                  inputMode="numeric"
                                  min={0}
                                  max={info?.open}
                                  className="w-20 text-right tabular-nums"
                                  aria-label={t('parcels.quantityFor', {
                                    title: info?.title ?? '',
                                    label: parcel.label,
                                  })}
                                  aria-invalid={fieldState.invalid}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      );
                    })}
                    {form.formState.errors.parcels?.[index]?.lines?.message ? (
                      <p role="alert" className="text-destructive text-sm">
                        {form.formState.errors.parcels[index].lines.message}
                      </p>
                    ) : null}
                  </fieldset>
                ) : null}
                {locked && parcel.lines.length > 0 ? (
                  <p className="text-muted-foreground text-xs">
                    {parcel.lines
                      .map(
                        (item) =>
                          `${item.quantity} × ${lines.find((l) => l.id === item.lineId)?.title ?? t('parcels.unknownLine')}`,
                      )
                      .join(', ')}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ol>
        {parcelsError ? (
          <p role="alert" className="text-destructive text-sm">
            {parcelsError}
          </p>
        ) : null}
        {showDelay ? <DelayReasonField run={target.run} /> : null}
      </ActionDialog>
    </Form>
  );
}
